import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, ExternalLink, RefreshCw } from 'lucide-react'
import { fetchOnboardingPjById, finalizeOnboarding } from '../../services/api'
import Loader from '../../components/Loader'
import {
	Alert,
	Badge,
	Button,
	Card,
	CopyableId,
	DetailGrid,
	DetailItem,
	EmptyState,
	JsonViewer,
	PageHeader,
	formatDateTime,
	formatDocument,
	statusTone,
} from '../../components/ui'

const OnboardingDetails = () => {
	const { id } = useParams()
	const [onboarding, setOnboarding] = useState(null)
	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState('')
	const [feedback, setFeedback] = useState('')
	const [showRaw, setShowRaw] = useState(false)
	const [isFinalizing, setIsFinalizing] = useState(false)

	const load = useCallback(async () => {
		setIsLoading(true)
		setError('')
		try {
			setOnboarding(await fetchOnboardingPjById(id))
		} catch (err) {
			setError(err.message)
			setOnboarding(null)
		} finally {
			setIsLoading(false)
		}
	}, [id])

	useEffect(() => {
		load()
	}, [load])

	const handleFinalize = async () => {
		const confirmado = window.confirm(
			'Finalizar o onboarding? Dependendo do status atual isso envia os dados para o BaaS.'
		)
		if (!confirmado) return

		setIsFinalizing(true)
		setError('')
		setFeedback('')
		try {
			await finalizeOnboarding(id)
			setFeedback('Onboarding finalizado')
			await load()
		} catch (err) {
			setError(err.message)
		} finally {
			setIsFinalizing(false)
		}
	}

	if (isLoading) {
		return (
			<div className="flex h-64 items-center justify-center">
				<Loader size="lg" />
			</div>
		)
	}

	const legalRepresentative = onboarding?.legalRepresentative
	const partners = onboarding?.partners || []
	const documents = onboarding?.documents || []

	return (
		<div>
			<Link
				to="/onboardings"
				className="mb-4 inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
			>
				<ArrowLeft className="mr-1 h-4 w-4" />
				Voltar para onboardings
			</Link>

			<PageHeader
				title={onboarding?.legalName || 'Onboarding'}
				subtitle={`GET /backoffice/onboardings/pj/${id}`}
				actions={
					<>
						<Button variant="secondary" onClick={() => setShowRaw((prev) => !prev)}>
							{showRaw ? 'Ocultar JSON' : 'Ver JSON'}
						</Button>
						<Button variant="secondary" onClick={load}>
							<RefreshCw className="h-4 w-4" />
							Recarregar
						</Button>
						<Button variant="secondary" onClick={handleFinalize} disabled={isFinalizing}>
							{isFinalizing ? (
								<>
									<Loader size="sm" />
									Finalizando...
								</>
							) : (
								'Finalizar'
							)}
						</Button>
					</>
				}
			/>

			<Alert onDismiss={() => setError('')}>{error}</Alert>
			<Alert type="success" onDismiss={() => setFeedback('')}>
				{feedback}
			</Alert>

			{!onboarding ? (
				<EmptyState message="Onboarding nao encontrado" />
			) : (
				<div className="space-y-6">
					<Card title="Dados do cadastro">
						<DetailGrid>
							<DetailItem label="ID">
								<CopyableId value={onboarding.id} />
							</DetailItem>
							<DetailItem label="Status">
								<Badge tone={statusTone(onboarding.status)}>{onboarding.status}</Badge>
							</DetailItem>
							<DetailItem label="Tipo de cliente">{onboarding.clientType}</DetailItem>
							<DetailItem label="Razao social">{onboarding.legalName}</DetailItem>
							<DetailItem label="Documento">
								{formatDocument(onboarding.document)}
							</DetailItem>
							<DetailItem label="Criado em">
								{formatDateTime(onboarding.createdAt)}
							</DetailItem>
						</DetailGrid>
					</Card>

					<Card title="Representante legal">
						{!legalRepresentative ? (
							<EmptyState message="Sem representante legal informado" />
						) : (
							<div className="space-y-5">
								<DetailGrid>
									<DetailItem label="Nome">{legalRepresentative.name}</DetailItem>
									<DetailItem label="Documento">
										{formatDocument(legalRepresentative.document)}
									</DetailItem>
									<DetailItem label="Tipo">{legalRepresentative.clientType}</DetailItem>
									<DetailItem label="Link de biometria">
										{legalRepresentative.biometricLink ? (
											<a
												href={legalRepresentative.biometricLink}
												target="_blank"
												rel="noreferrer"
												className="inline-flex items-center gap-1 text-primary-600 hover:underline"
											>
												Abrir link
												<ExternalLink className="h-3.5 w-3.5" />
											</a>
										) : null}
									</DetailItem>
								</DetailGrid>

								{(legalRepresentative.documents || []).length > 0 && (
									<div className="overflow-x-auto">
										<table className="min-w-full divide-y divide-gray-200 text-sm">
											<thead className="bg-gray-50">
												<tr>
													<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
														Tipo
													</th>
													<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
														Lado
													</th>
													<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
														Analise imagem
													</th>
													<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
														Face match
													</th>
													<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
														Liveness
													</th>
													<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
														Resultado
													</th>
												</tr>
											</thead>
											<tbody className="divide-y divide-gray-200">
												{legalRepresentative.documents.map((doc) => (
													<tr key={doc.uuid || `${doc.documentType}-${doc.documentSide}`}>
														<td className="px-4 py-2 text-gray-900">{doc.documentType}</td>
														<td className="px-4 py-2 text-gray-500">{doc.documentSide}</td>
														<td className="px-4 py-2 text-gray-500">
															{doc.baasImageAnalysisStatus || '—'}
														</td>
														<td className="px-4 py-2 text-gray-500">
															{doc.faceMatchStatus || '—'}
														</td>
														<td className="px-4 py-2 text-gray-500">
															{doc.baasLivenessStatus || '—'}
														</td>
														<td className="px-4 py-2">
															{doc.baasImageAnalysisResult ? (
																<Badge tone={statusTone(doc.baasImageAnalysisResult)}>
																	{doc.baasImageAnalysisResult}
																</Badge>
															) : (
																'—'
															)}
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								)}
							</div>
						)}
					</Card>

					<Card title={`Quadro societario (${partners.length})`}>
						{partners.length === 0 ? (
							<EmptyState message="Nenhum socio informado" />
						) : (
							<div className="overflow-x-auto">
								<table className="min-w-full divide-y divide-gray-200 text-sm">
									<thead className="bg-gray-50">
										<tr>
											<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
												Nome
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
												Documento
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
												Tipo
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
												Representante legal
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
												Biometria
											</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-gray-200">
										{partners.map((partner) => (
											<tr key={partner.id}>
												<td className="px-4 py-2 text-gray-900">{partner.name}</td>
												<td className="px-4 py-2 text-gray-500">
													{formatDocument(partner.document)}
												</td>
												<td className="px-4 py-2 text-gray-500">{partner.clientType}</td>
												<td className="px-4 py-2">
													<Badge tone={partner.legalRepresentative ? 'green' : 'gray'}>
														{partner.legalRepresentative ? 'Sim' : 'Nao'}
													</Badge>
												</td>
												<td className="px-4 py-2">
													{partner.biometricLink ? (
														<a
															href={partner.biometricLink}
															target="_blank"
															rel="noreferrer"
															className="inline-flex items-center gap-1 text-primary-600 hover:underline"
														>
															Abrir
															<ExternalLink className="h-3.5 w-3.5" />
														</a>
													) : (
														'—'
													)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</Card>

					<Card title={`Documentos (${documents.length})`}>
						{documents.length === 0 ? (
							<EmptyState message="Nenhum documento enviado" />
						) : (
							<div className="overflow-x-auto">
								<table className="min-w-full divide-y divide-gray-200 text-sm">
									<thead className="bg-gray-50">
										<tr>
											<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
												Tipo
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
												Descricao
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
												UUID
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
												Recebido em
											</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-gray-200">
										{documents.map((doc) => (
											<tr key={doc.id}>
												<td className="px-4 py-2 text-gray-900">{doc.documentType}</td>
												<td className="px-4 py-2 text-gray-500">{doc.description || '—'}</td>
												<td className="px-4 py-2">
													<CopyableId value={doc.uuid} />
												</td>
												<td className="px-4 py-2 text-gray-500">
													{formatDateTime(doc.receivedDate)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</Card>

					{showRaw && (
						<Card title="Resposta bruta">
							<JsonViewer data={onboarding} />
						</Card>
					)}
				</div>
			)}
		</div>
	)
}

export default OnboardingDetails
